import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { renderPostHtml } from "../../utils/postContent";
import { getTextOnlyLength, trimTextPreservingUrls } from "../../utils/urlUtils";


const DEFAULT_LIMIT = 255;
const MAX_SHRINK_STEPS = 12;
const MIN_PREVIEW_CHARS = 24;

const DANGLING_WORDS = new Set([
  "a", "an", "the", "and", "or", "but", "so", "if", "as", "at", "by", "for",
  "from", "in", "into", "of", "on", "onto", "over", "to", "up", "with", "within",
  "is", "was", "are", "were", "be", "been", "being", "that", "this", "these",
  "those", "it", "its", "their", "our", "your", "his", "her", "my", "we", "they",
  "he", "she", "i", "you", "than", "then", "when", "while", "about",
]);

const trimAtWordBoundary = (text, limit) => {
  if (!text) return "";
  if (text.length <= limit) return text;
  const trimmed = text.substring(0, limit);
  const lastSpace = trimmed.lastIndexOf(" ");
  return lastSpace > 0 ? trimmed.substring(0, lastSpace) : trimmed;
};

const dropTrailingWords = (text, times) => {
  let out = text;
  for (let i = 0; i < times; i += 1) {
    const idx = out.trimEnd().lastIndexOf(" ");
    if (idx < MIN_PREVIEW_CHARS) break;
    out = out.slice(0, idx);
  }
  return out;
};

const dropDanglingTail = (text) => {
  let out = text.trimEnd();
  for (let i = 0; i < 6; i += 1) {
    out = out.replace(/[\s.,;:!?—–-]+$/u, "");
    const idx = out.lastIndexOf(" ");
    if (idx < MIN_PREVIEW_CHARS) break;
    const lastWord = out
      .slice(idx + 1)
      .replace(/<[^>]*>/g, "")
      .replace(/[^\p{L}\p{N}']/gu, "")
      .toLowerCase();
    if (!lastWord || DANGLING_WORDS.has(lastWord)) {
      out = out.slice(0, idx);
    } else {
      break;
    }
  }
  return out.trimEnd();
};

const BLOCK_TAGS = new Set(["P", "DIV", "LI", "UL", "OL", "BLOCKQUOTE"]);

const appendInline = (html, affordanceHtml) => {
  if (typeof window === "undefined" || !window.DOMParser) {
    return `${html}${affordanceHtml}`;
  }
  const doc = new window.DOMParser().parseFromString(
    `<div id="qushi-caption-root">${html}</div>`,
    "text/html"
  );
  const root = doc.getElementById("qushi-caption-root");
  if (!root) return `${html}${affordanceHtml}`;

  let target = root;
  for (let depth = 0; depth < 8; depth += 1) {
    while (
      target.lastChild &&
      ((target.lastChild.nodeType === 3 && !target.lastChild.nodeValue.trim()) ||
        (target.lastChild.nodeType === 1 &&
          (target.lastChild.tagName === "BR" ||
            (BLOCK_TAGS.has(target.lastChild.tagName) &&
              !target.lastChild.textContent.trim()))))
    ) {
      target.removeChild(target.lastChild);
    }
    const last = target.lastElementChild;
    if (
      last &&
      BLOCK_TAGS.has(last.tagName) &&
      last.textContent.trim() &&
      target.lastChild === last
    ) {
      target = last;
    } else {
      break;
    }
  }

  target.insertAdjacentHTML("beforeend", affordanceHtml);
  return root.innerHTML;
};

const AFFORDANCE_CLASS = "post-caption-toggle";

// `whitespace-nowrap` is the load-bearing part: without it the browser breaks
// the affordance at its own inner space, leaving "…" on the caption's last line
// and dropping "more" alone onto the next one — which is exactly what the client
// rejected. Kept as one unbreakable unit it either fits beside the text or moves
// wholesale, and the fit check below can then see that it moved and shrink.
const affordanceMarkup = (label) =>
  `<span class="${AFFORDANCE_CLASS} text-[#888888] cursor-pointer whitespace-nowrap" role="button" tabindex="0">${label}</span>`;

const PostCaption = ({
  description,
  hideUrl = false,
  expanded = false,
  onToggle,
  limit = DEFAULT_LIMIT,
}) => {
  const rootRef = useRef(null);
  const shrinkAttempts = useRef(0);
  const [shrink, setShrink] = useState(0);

  const visibleLength = hideUrl
    ? getTextOnlyLength(description)
    : description?.length || 0;
  const isTruncated = visibleLength > limit;

  useEffect(() => {
    shrinkAttempts.current = 0;
    setShrink(0);
  }, [description, limit, hideUrl, expanded]);

  useEffect(() => {
    const parent = rootRef.current?.parentElement;
    if (!parent || typeof window === "undefined" || !window.ResizeObserver) {
      return undefined;
    }
    let lastWidth = parent.getBoundingClientRect().width;
    const observer = new window.ResizeObserver(() => {
      const width = parent.getBoundingClientRect().width;
      if (Math.abs(width - lastWidth) < 1) return;
      lastWidth = width;
      shrinkAttempts.current = 0;
      setShrink(0);
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  let body;
  if (expanded || !isTruncated) {
    body = description || "";
  } else {
    const base = hideUrl
      ? trimTextPreservingUrls(description, limit)
      : trimAtWordBoundary(description, limit);
    body = dropDanglingTail(dropTrailingWords(base, shrink));
  }

  const withoutUrls = hideUrl
    ? String(body).replace(/https?:\/\/[^\s<]+/g, "")
    : String(body);

  let finalHtml = renderPostHtml(withoutUrls);
  if (isTruncated) {
    finalHtml = appendInline(
      finalHtml,
      affordanceMarkup(expanded ? " less" : "… more")
    );
  }

  useLayoutEffect(() => {
    if (expanded || !isTruncated) return;
    if (shrinkAttempts.current >= MAX_SHRINK_STEPS) return;
    const root = rootRef.current;
    if (!root) return;
    const toggle = root.querySelector(`.${AFFORDANCE_CLASS}`);
    if (!toggle) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let lastText = null;
    let node = walker.nextNode();
    while (node) {
      if (toggle.contains(node)) break;
      if (node.nodeValue && node.nodeValue.trim()) lastText = node;
      node = walker.nextNode();
    }
    if (!lastText) return;

    const range = document.createRange();
    range.setStart(lastText, Math.max(0, lastText.nodeValue.length - 1));
    range.setEnd(lastText, lastText.nodeValue.length);
    const charRect = range.getBoundingClientRect();

    // Measure the affordance's LAST line box, not its bounding box. A bounding
    // box starts wherever the element starts, so a "… more" that began beside
    // the text and spilled over still reported the caption's own line and the
    // fit looked fine. getClientRects() gives one rect per line, so the last one
    // is where the affordance actually ends up.
    const toggleRects = toggle.getClientRects();
    const toggleRect = toggleRects[toggleRects.length - 1];
    if (!charRect.height || !toggleRect || !toggleRect.height) return;

    // Two ways it can fail, both meaning "not beside the text": it sits on a
    // later line, or it occupies more than one line itself.
    const wrapped =
      toggleRects.length > 1 ||
      toggleRect.top - charRect.top > charRect.height * 0.5;

    if (wrapped) {
      shrinkAttempts.current += 1;
      setShrink((value) => value + 1);
    }
  });

  const handleActivate = useCallback(
    (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest(`.${AFFORDANCE_CLASS}`)) return;
      event.preventDefault();
      event.stopPropagation();
      if (onToggle) onToggle();
    },
    [onToggle]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      handleActivate(event);
    },
    [handleActivate]
  );

  if (!body) return null;

  return (
    <span
      ref={rootRef}
      className="post-content"
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
};

export default PostCaption;
